import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { ChartBar, Warning, TrendUp, TrendDown, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Portfolio Profitability Heatmap — executive grid of every project
 * coloured by margin%, sorted so bleeding projects float to the top.
 *
 * Data: GET /api/finance/portfolio-heatmap
 *   → rows[] with {id, name, code, status, contract_value, total_cost,
 *                  gross_margin, gross_margin_pct, budget_used_pct, overrun}
 *   → totals.{project_count, total_contract_value, total_cost,
 *             total_margin, portfolio_margin_pct, healthy, warning,
 *             bleeding, overrun_count}
 */
export default function PortfolioHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/portfolio-heatmap");
      setData(data);
    } catch { /* silent — non-finance users won't see this widget */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!data) return null;     // hide until we have data (silent on 403)
  const t = data.totals;

  // Tone for a project tile based on margin%
  const tileTone = (m) => {
    if (m == null) return { bg: "bg-slate-100",   border: "border-slate-300",  text: "text-slate-700" };
    if (m < 0)     return { bg: "bg-red-100",     border: "border-red-300",    text: "text-red-800" };
    if (m < 15)    return { bg: "bg-amber-100",   border: "border-amber-300",  text: "text-amber-800" };
    if (m < 30)    return { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800" };
    return            { bg: "bg-emerald-200",   border: "border-emerald-400", text: "text-emerald-900" };
  };

  return (
    <section className="dc-card overflow-hidden" data-testid="portfolio-heatmap">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <div>
          <div className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-slate-500">Executive view</div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Portfolio Profitability Heatmap</h2>
          <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
            {t.project_count} projects · sorted by margin (bleeding first)
          </div>
        </div>
        <button
          onClick={load}
          className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="heatmap-refresh"
        >
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} /> refresh
        </button>
      </div>

      {/* Portfolio totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-5 py-3 border-b border-slate-100" data-testid="heatmap-totals">
        <Totals label="Portfolio margin" value={`${t.portfolio_margin_pct}%`} tone={t.portfolio_margin_pct >= 15 ? "emerald" : t.portfolio_margin_pct >= 0 ? "amber" : "red"} testid="totals-margin" />
        <Totals label="Total value" value={formatCurrency(t.total_contract_value)} testid="totals-value" />
        <Totals label="Healthy ≥ 15%" value={t.healthy} tone="emerald" icon={TrendUp} testid="totals-healthy" />
        <Totals label="Warning 0–15%" value={t.warning} tone="amber" testid="totals-warning" />
        <Totals label="Bleeding < 0%" value={t.bleeding} tone="red" icon={TrendDown} testid="totals-bleeding" />
      </div>

      {/* Heatmap grid */}
      {data.rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="heatmap-empty">
          No projects yet — register a project to start tracking portfolio margins.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="heatmap-grid">
          {data.rows.map((r) => {
            const tone = tileTone(r.gross_margin_pct);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/projects?focus=${r.id}`)}
                className={`group rounded-lg border ${tone.border} ${tone.bg} px-3 py-2.5 text-left transition hover:shadow-md hover:scale-[1.01]`}
                data-testid={`heatmap-tile-${r.id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-[10px] font-mono-alt uppercase tracking-wider ${tone.text}`}>{r.code || ""}</span>
                  {r.overrun && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-red-200 px-1 py-0.5 text-[9px] font-bold text-red-800 uppercase" data-testid={`heatmap-overrun-${r.id}`}>
                      <Warning size={8} weight="fill" /> Overrun
                    </span>
                  )}
                </div>
                <h4 className={`mt-0.5 font-display text-sm font-semibold ${tone.text} line-clamp-2 break-words`}>{r.name}</h4>
                <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${tone.text}`} data-testid={`heatmap-margin-${r.id}`}>
                  {r.gross_margin_pct == null ? "—" : `${r.gross_margin_pct}%`}
                </div>
                <div className="text-[10px] text-slate-600 font-mono-alt tabular-nums">
                  {formatCurrency(r.gross_margin)} margin
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] font-mono-alt text-slate-600">
                  <span>{formatCurrency(r.contract_value)} value</span>
                  <span className={r.budget_used_pct > 100 ? "text-red-700 font-bold" : r.budget_used_pct > 85 ? "text-amber-700" : ""}>
                    {r.budget_used_pct != null ? `${r.budget_used_pct}% used` : "—"}
                  </span>
                </div>
                {r.budget_used_pct != null && (
                  <div className="mt-1 h-1 rounded bg-white/60 overflow-hidden">
                    <div className={`h-full ${r.budget_used_pct > 100 ? "bg-red-500" : r.budget_used_pct > 85 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, r.budget_used_pct)}%` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="border-t border-slate-100 px-5 py-2 flex items-center gap-3 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500" data-testid="heatmap-legend">
        <LegendDot tone="red" label="< 0%" />
        <LegendDot tone="amber" label="0–15%" />
        <LegendDot tone="emerald" label="15–30%" />
        <LegendDot tone="emerald-dark" label="≥ 30%" />
        <span className="ml-auto inline-flex items-center gap-1"><ChartBar size={10} /> click any tile → open project</span>
      </div>
    </section>
  );
}

function Totals({ label, value, tone, icon: Icon, testid }) {
  const TONE = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-800",
    red: "border-red-200 bg-red-50/60 text-red-800",
    blue: "border-blue-200 bg-blue-50/60 text-blue-800",
  }[tone] || "border-slate-200 bg-slate-50/60 text-slate-700";
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${TONE}`} data-testid={testid}>
      <div className="overline text-[9px]">{label}</div>
      <div className="flex items-baseline gap-1.5">
        {Icon && <Icon size={11} weight="fill" />}
        <span className="font-mono-alt tabular-nums text-base font-bold">{value}</span>
      </div>
    </div>
  );
}

function LegendDot({ tone, label }) {
  const TONE = {
    red: "bg-red-100 border-red-300",
    amber: "bg-amber-100 border-amber-300",
    emerald: "bg-emerald-100 border-emerald-300",
    "emerald-dark": "bg-emerald-200 border-emerald-400",
  }[tone] || "bg-slate-100 border-slate-300";
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-3 w-3 rounded border ${TONE}`} />
      {label}
    </span>
  );
}
